import type { ListResponse } from 'Services/ProductManagement/types'
import type { LoadingStates, Translation } from 'Store'
import { createAsyncThunkWithErrorHandling, getAxiosInstance } from 'Util/ApiClient'
import actionGenerator from 'Util/actionGenerator'
import { createSlice } from '@reduxjs/toolkit'

export enum DurationUnit {
  DAYS = 'days',
  HOURS = 'hours',
  MINUTES = 'minutes',
  SECONDS = 'seconds'
}

export enum DaysOfTheWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY'
}

export interface Validity {
  days_of_week: DaysOfTheWeek[]
  exceptions: string[]
  time_of_day_from: string | null
  time_of_day_until: string | null
  title: string
  valid_from: string
  valid_now: boolean
  valid_until: string
  zone_id: string | null
}

export interface PricingPlan {
  active_period: Validity | null
  custom_fields: Record<string, unknown>
  description: string
  domicile_class: string | null
  pricings: {
    currency: string
    description: string
    price: number
    title: string
    vat_included: boolean
  }[]
  visible_period: Validity | null
  title: string
  usage_validity_duration: string | null
  vat_code?: string
  zone_map: string[]
}

export enum PricingPlanTabType {
  PRICINGS = 'pricings',
  EXCHANGE_PERIOD = 'exchange_period'
}

export enum AllowedActivationMethods {
  FIRST_USE = 'FIRST_USE',
  PURCHASE = 'PURCHASE',
  TIMED = 'TIMED'
}

export enum ProductUsages {
  SINGLE_USER = 'SINGLE_USER',
  CONCURRENT_USERS = 'CONCURRENT_USERS'
}

export enum ProductTabType {
  DETAILS = 'details',
  TARIFFS = 'tariffs',
  AVAILABILITY = 'availability'
}

export interface Product {
  activation_window: {
    duration: string | null
    period: Validity[]
  }
  allowed_tokens: string[]
  age_group_id: string
  api_client_read: string[]
  api_client_write: string[]
  created_timestamp: string
  custom_fields: Record<string, unknown>
  customer_group: string
  distribution_channel: string
  id: string
  modified_timestamp: string
  max_purchase_advance_duration: string | null
  owner_client_id: string
  pricing_plans: PricingPlan[]
  product_code: string
  product_group: string
  product_owner: string
  visible_period: Validity[]
  source: string
  translations: Translation[]
  usage_validity_duration: string | null
  usage_trigger_event: string[]
  vat_code: string
  strong_identification_required: boolean
  shared_usage: string
  catalog_ids: string[]
  print_zero_price: boolean
}

export interface ProductState {
  total_results: number
  next_page: string | null
  previous_page: string | null
  products: Product[]
  allProducts: Product[]
  tariffProducts: Product[]
  allTariffProducts: Product[]
  allTariffCatalogProducts: Product[]
  singleProduct: Product | null
  loading: LoadingStates
  allProductsLoading: LoadingStates
  tariffProductsLoading: LoadingStates
  allTariffProductsLoading: LoadingStates
  allTariffCatalogProductsLoading: LoadingStates
  singleLoading: LoadingStates
  deleteLoading: LoadingStates
  submitLoading: LoadingStates
}

const initialState: ProductState = {
  total_results: 0,
  next_page: null,
  previous_page: null,
  products: [],
  allProducts: [],
  tariffProducts: [],
  allTariffProducts: [],
  allTariffCatalogProducts: [],
  singleProduct: null,
  loading: 'idle',
  allProductsLoading: 'idle',
  tariffProductsLoading: 'idle',
  allTariffProductsLoading: 'idle',
  allTariffCatalogProductsLoading: 'idle',
  singleLoading: 'idle',
  deleteLoading: 'idle',
  submitLoading: 'idle'
}

export interface FetchProductListParams {
  filter?: string
  search_text?: string
  sort_by?: string
  sort_order?: string
  start_index?: string
  count?: number
  bypass_pagination?: boolean
  has_no_tariff?: boolean
  in_tariff?: string
  in_catalog?: string
}

export enum ProductDeleteModes {
  SAFE = 'SAFE',
  FORCE = 'FORCE',
  CASCADE = 'CASCADE'
}

export interface ProductDeleteParams {
  mode?: ProductDeleteModes
}

const fetchProducts = createAsyncThunkWithErrorHandling<
  ListResponse<Product>,
  FetchProductListParams | undefined
>('product/fetchProducts', async data => {
  const { ...params } = data
  const response = await getAxiosInstance().get<ListResponse<Product>>('/api/v1/product/', {
    params
  })
  return response.data
})

const fetchAllProducts = createAsyncThunkWithErrorHandling<
  ListResponse<Product>,
  FetchProductListParams | undefined
>('product/fetchAllProducts', async data => {
  const { ...params } = data
  const response = await getAxiosInstance().get<ListResponse<Product>>('/api/v1/product/', {
    params
  })
  return response.data
})

const fetchTariffProducts = createAsyncThunkWithErrorHandling<
  ListResponse<Product>,
  FetchProductListParams | undefined
>('product/fetchTariffProducts', async data => {
  const { ...params } = data
  const response = await getAxiosInstance().get<ListResponse<Product>>('/api/v1/product/', {
    params
  })
  return response.data
})

const fetchAllTariffProducts = createAsyncThunkWithErrorHandling<
  ListResponse<Product>,
  FetchProductListParams | undefined
>('product/fetchAllTariffProducts', async data => {
  const { ...params } = data
  const response = await getAxiosInstance().get<ListResponse<Product>>('/api/v1/product/', {
    params
  })
  return response.data
})

const fetchAllTariffCatalogProducts = createAsyncThunkWithErrorHandling<
  ListResponse<Product>,
  FetchProductListParams | undefined
>('product/fetchAllTariffCatalogProducts', async data => {
  const { ...params } = data
  const response = await getAxiosInstance().get<ListResponse<Product>>('/api/v1/product/', {
    params
  })
  return response.data
})

const fetchSingleProduct = createAsyncThunkWithErrorHandling<Product, { id: string }>(
  'product/fetchSingleProduct',
  async data => {
    const response = await getAxiosInstance().get<Product>(`/api/v1/product/${data.id}/`)
    return response.data
  }
)

const createSingleProduct = createAsyncThunkWithErrorHandling<Product, Partial<Product>>(
  'product/createSingleProduct',
  async data => {
    const response = await getAxiosInstance().post<Product>('/api/v1/product/', data)
    return response.data
  }
)

const updateSingleProduct = createAsyncThunkWithErrorHandling<
  Product,
  { id: string } & Partial<Product>
>('product/updateSingleProduct', async data => {
  const { id, ...fields } = data
  const response = await getAxiosInstance().put<Product>(`/api/v1/product/${id}/`, fields)
  return response.data
})

type ProductDeleteResponse = Record<string, never>

const deleteSingleProduct = createAsyncThunkWithErrorHandling<
  ProductDeleteResponse,
  { id: string } & ProductDeleteParams
>('product/deleteSingleProduct', async data => {
  const { id, ...params } = data
  await getAxiosInstance().delete(`/api/v1/product/${id}/`, { params })
  return {}
})

const genericActions = actionGenerator('product')

const productSlice = createSlice({
  name: genericActions.name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      /* Product list */
      .addCase(fetchProducts.fulfilled, (state, { payload }) => {
        state.products = payload.resources
        state.total_results = payload.total_results
        state.next_page = payload.next_page
        state.previous_page = payload.previous_page
        state.loading = 'fulfilled'
      })
      .addCase(fetchProducts.pending, state => {
        state.products = initialState.products
        state.total_results = initialState.total_results
        state.next_page = initialState.next_page
        state.previous_page = initialState.previous_page
        state.loading = 'pending'
      })
      .addCase(fetchProducts.rejected, state => {
        state.loading = 'rejected'
      })

      /* All products */
      .addCase(fetchAllProducts.fulfilled, (state, { payload }) => {
        state.allProducts = payload.resources
        state.allProductsLoading = 'fulfilled'
      })
      .addCase(fetchAllProducts.pending, state => {
        state.allProducts = initialState.allProducts
        state.allProductsLoading = 'pending'
      })
      .addCase(fetchAllProducts.rejected, state => {
        state.allProductsLoading = 'rejected'
      })

      /* Tariff product list */
      .addCase(fetchTariffProducts.fulfilled, (state, { payload }) => {
        state.tariffProducts = payload.resources
        state.total_results = payload.total_results
        state.next_page = payload.next_page
        state.previous_page = payload.previous_page
        state.tariffProductsLoading = 'fulfilled'
      })
      .addCase(fetchTariffProducts.pending, state => {
        state.tariffProducts = initialState.tariffProducts
        state.total_results = initialState.total_results
        state.next_page = initialState.next_page
        state.previous_page = initialState.previous_page
        state.tariffProductsLoading = 'pending'
      })
      .addCase(fetchTariffProducts.rejected, state => {
        state.tariffProductsLoading = 'rejected'
      })

      /* All tariff products */
      .addCase(fetchAllTariffProducts.fulfilled, (state, { payload }) => {
        state.allTariffProducts = payload.resources
        state.allTariffProductsLoading = 'fulfilled'
      })
      .addCase(fetchAllTariffProducts.pending, state => {
        state.allTariffProducts = initialState.allTariffProducts
        state.allTariffProductsLoading = 'pending'
      })
      .addCase(fetchAllTariffProducts.rejected, state => {
        state.allTariffProductsLoading = 'rejected'
      })

      /* All tariff catalog products */
      .addCase(fetchAllTariffCatalogProducts.fulfilled, (state, { payload }) => {
        state.allTariffCatalogProducts = payload.resources
        state.allTariffCatalogProductsLoading = 'fulfilled'
      })
      .addCase(fetchAllTariffCatalogProducts.pending, state => {
        state.allTariffCatalogProducts = initialState.allTariffProducts
        state.allTariffCatalogProductsLoading = 'pending'
      })
      .addCase(fetchAllTariffCatalogProducts.rejected, state => {
        state.allTariffCatalogProductsLoading = 'rejected'
      })

      /* Single product */
      .addCase(fetchSingleProduct.fulfilled, (state, { payload }) => {
        state.singleProduct = payload
        state.singleLoading = 'fulfilled'
      })
      .addCase(fetchSingleProduct.pending, state => {
        state.singleProduct = initialState.singleProduct
        state.singleLoading = 'pending'
      })
      .addCase(fetchSingleProduct.rejected, state => {
        state.singleLoading = 'rejected'
      })

      /* Update single product */
      .addCase(updateSingleProduct.fulfilled, (state, { payload }) => {
        state.singleProduct = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(updateSingleProduct.pending, state => {
        state.submitLoading = 'pending'
      })
      .addCase(updateSingleProduct.rejected, state => {
        state.submitLoading = 'rejected'
      })

      /* Create single product */
      .addCase(createSingleProduct.fulfilled, (state, { payload }) => {
        state.singleProduct = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(createSingleProduct.pending, state => {
        state.submitLoading = 'pending'
      })
      .addCase(createSingleProduct.rejected, state => {
        state.submitLoading = 'rejected'
      })

      /* Delete single product */
      .addCase(deleteSingleProduct.fulfilled, state => {
        state.deleteLoading = 'fulfilled'
      })
      .addCase(deleteSingleProduct.pending, state => {
        state.deleteLoading = 'pending'
      })
      .addCase(deleteSingleProduct.rejected, state => {
        state.deleteLoading = 'rejected'
      })
  }
})

export default productSlice.reducer

export {
  createSingleProduct,
  deleteSingleProduct,
  fetchAllProducts,
  fetchAllTariffCatalogProducts,
  fetchAllTariffProducts,
  fetchProducts,
  fetchSingleProduct,
  fetchTariffProducts,
  updateSingleProduct
}
